// @ts-nocheck
import { useMemo } from 'react';
import { Box, Group, Progress, Stack, Text } from '@/components/ui';
import { IconCheck, IconX } from '@tabler/icons-react';

export type StrengthVariant = 'bar' | 'steps' | 'requirements';

interface PasswordStrengthProps {
  password: string;
  variant?: StrengthVariant;
  showLabel?: boolean;
  showRequirements?: boolean;
}

interface Requirement {
  label: string;
  test: (password: string) => boolean;
}

const requirements: Requirement[] = [
  { label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { label: 'Contains uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { label: 'Contains lowercase letter', test: (p) => /[a-z]/.test(p) },
  { label: 'Contains number', test: (p) => /[0-9]/.test(p) },
  { label: 'Contains special character', test: (p) => /[!@#$%^&*(),.?":{}|<>]/.test(p) },
];

function getStrength(password: string): number {
  if (!password) return 0;
  const passedRequirements = requirements.filter((req) => req.test(password)).length;
  return (passedRequirements / requirements.length) * 100;
}

function getStrengthLabel(strength: number): { label: string; color: string } {
  if (strength === 0) return { label: '', color: 'gray' };
  if (strength < 40) return { label: 'Weak', color: 'red' };
  if (strength < 60) return { label: 'Fair', color: 'orange' };
  if (strength < 80) return { label: 'Good', color: 'yellow' };
  return { label: 'Strong', color: 'green' };
}

function RequirementItem({ meets, label }: { meets: boolean; label: string }) {
  return (
    <Group gap="xs">
      {meets ? (
        <IconCheck size={14} color="var(--mantine-color-green-6)" />
      ) : (
        <IconX size={14} color="var(--mantine-color-red-6)" />
      )}
      <Text size="xs" c={meets ? 'green' : 'dimmed'}>
        {label}
      </Text>
    </Group>
  );
}

export default function PasswordStrength({
  password,
  variant = 'bar',
  showLabel = true,
  showRequirements = false,
}: PasswordStrengthProps) {
  const strength = useMemo(() => getStrength(password), [password]);
  const { label, color } = getStrengthLabel(strength);

  const renderBar = () => (
    <Stack gap={4}>
      <Progress value={strength} color={color} size="sm" radius="xl" />
      {showLabel && label && (
        <Text size="xs" c={color} ta="right">
          {label}
        </Text>
      )}
    </Stack>
  );

  const renderSteps = () => {
    const passedCount = requirements.filter((req) => req.test(password)).length;
    return (
      <Stack gap={4}>
        <Group gap={4}>
          {requirements.map((_, index) => (
            <Box
              key={index}
              style={{
                flex: 1,
                height: 4,
                borderRadius: 2,
                backgroundColor:
                  index < passedCount
                    ? `var(--mantine-color-${color}-6)`
                    : 'var(--mantine-color-gray-3)',
                transition: 'background-color 0.2s ease',
              }}
            />
          ))}
        </Group>
        {showLabel && label && (
          <Text size="xs" c={color} ta="right">
            {label}
          </Text>
        )}
      </Stack>
    );
  };

  const renderRequirements = () => (
    <Stack gap="xs">
      {requirements.map((req) => (
        <RequirementItem key={req.label} meets={req.test(password)} label={req.label} />
      ))}
    </Stack>
  );

  return (
    <Stack gap="xs">
      {variant === 'bar' && renderBar()}
      {variant === 'steps' && renderSteps()}
      {(variant === 'requirements' || showRequirements) && renderRequirements()}
    </Stack>
  );
}
